import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  User,
  Camera,
  MapPin,
  Globe,
  Instagram,
  Twitter,
  Save,
  LogOut,
  ImagePlus,
  Trash2,
  Star,
  AlertCircle,
  CheckCircle,
  Loader2,
  Upload,
  X,
  Coins,
  Plus,
  Pencil,
  Lock,
  Unlock,
  ShoppingCart,
  Receipt,
  Calendar,
  DollarSign,
  Clock,
  XCircle,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Users,
  UserPlus,
  UserMinus,
  UserCheck,
  TrendingUp,
  Wallet,
  AtSign,
  ExternalLink,
  Share2,
  Copy,
  Gift,
  Banknote,
} from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { usePublicAuth } from "../../context/PublicAuthContext";
import axios from "axios";
import BuyCreditsModal from "../../components/BuyCreditsModal";
import { compressImage, compressImageToBase64, compressionPresets } from "../../utils/imageCompression";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get correct image data URI
const getImageSrc = (base64Data) => {
  if (!base64Data) return null;
  if (base64Data.startsWith("data:")) return base64Data;
  
  // Detect image type from base64 signature
  if (base64Data.startsWith("UklGR")) {
    return `data:image/webp;base64,${base64Data}`;
  } else if (base64Data.startsWith("/9j/")) {
    return `data:image/jpeg;base64,${base64Data}`;
  } else if (base64Data.startsWith("iVBOR")) {
    return `data:image/png;base64,${base64Data}`;
  }
  return `data:image/jpeg;base64,${base64Data}`;
};

// Helper to get auth headers
// Configure axios to send cookies with all requests
axios.defaults.withCredentials = true;

// Auth headers are no longer needed - cookies are sent automatically
const getAuthHeaders = () => {
  return {}; // Empty - auth is now via httpOnly cookies
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, loading, logout, updateProfile, uploadAvatar, uploadHeader, refreshUser } = usePublicAuth();
  
  const [activeTab, setActiveTab] = useState("profile"); // profile, content, creator
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  
  // Username state
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [savingUsername, setSavingUsername] = useState(false);
  const usernameCheckTimeout = useRef(null);
  
  const [formData, setFormData] = useState({
    name: "",
    bio: "",
    location: "",
    website: "",
    instagram: "",
    twitter: "",
    tiktok: "",
    onlyfans: "",
  });

  const avatarInputRef = useRef(null);
  const headerInputRef = useRef(null);

  // Content state
  const [content, setContent] = useState([]);
  const [loadingContent, setLoadingContent] = useState(false);
  const [uploadingContent, setUploadingContent] = useState(false);
  const contentInputRef = useRef(null);
  
  // Content pagination state
  const [contentPage, setContentPage] = useState(0);
  const [contentHasMore, setContentHasMore] = useState(true);
  const [loadingMoreContent, setLoadingMoreContent] = useState(false);
  const contentLoaderRef = useRef(null);
  const CONTENT_PAGE_SIZE = 12;
  
  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploadCategory, setUploadCategory] = useState("free");
  const [uploadCost, setUploadCost] = useState(0);
  const [customCost, setCustomCost] = useState("");
  
  // Edit content modal state
  const [showEditContentModal, setShowEditContentModal] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [editContentForm, setEditContentForm] = useState({ title: "", description: "", category: "free", cost: 0 });
  const [savingContent, setSavingContent] = useState(false);
  
  // Cost preset values
  const costPresets = [0, 10, 25, 50, 100, 200];

  // Unlocked content state (for fans)
  const [unlockedContent, setUnlockedContent] = useState([]);
  const [loadingUnlocked, setLoadingUnlocked] = useState(false);
  
  // Unlocked content pagination state
  const [unlockedPage, setUnlockedPage] = useState(0);
  const [unlockedHasMore, setUnlockedHasMore] = useState(true);
  const [loadingMoreUnlocked, setLoadingMoreUnlocked] = useState(false);
  const unlockedLoaderRef = useRef(null);
  const UNLOCKED_PAGE_SIZE = 3;

  // Unlocked content lightbox state
  const [selectedUnlockedIndex, setSelectedUnlockedIndex] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [lightboxImageLoading, setLightboxImageLoading] = useState(false);
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 3;
  const ZOOM_STEP = 0.5;

  // Purchases/Transactions state
  const [purchases, setPurchases] = useState([]);
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  const [purchaseStats, setPurchaseStats] = useState({ totalCredits: 0, totalSpent: 0 });

  // Social (Followers/Following) state
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loadingFollowers, setLoadingFollowers] = useState(false);
  const [loadingFollowing, setLoadingFollowing] = useState(false);
  const [socialTab, setSocialTab] = useState("followers"); // followers or following
  const [followCounts, setFollowCounts] = useState({ followers: 0, following: 0 });
  const [showSocialModal, setShowSocialModal] = useState(false);

  // Creator earnings state
  const [earnings, setEarnings] = useState([]);
  const [loadingEarnings, setLoadingEarnings] = useState(false);
  const [earningsStats, setEarningsStats] = useState({ totalEarnings: 0, currentBalance: 0, earningsCount: 0 });

  // Buy Credits modal state
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);

  // Featured creators state
  const [featuredCreators, setFeaturedCreators] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(false);

  // Invite state
  const [inviteCode, setInviteCode] = useState(null);
  const [invitedCount, setInvitedCount] = useState(0);
  const [inviteEnabled, setInviteEnabled] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);

  // Follower visibility state
  const [showFollowerCounts, setShowFollowerCounts] = useState(true);

  // Change Password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Creator request state
  const [creatorStatus, setCreatorStatus] = useState(null);
  const [loadingCreatorStatus, setLoadingCreatorStatus] = useState(false);
  const [requestingCreator, setRequestingCreator] = useState(false);
  const [creatorMessage, setCreatorMessage] = useState("");

  // Initialize form data when user loads
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        bio: user.bio || "",
        location: user.location || "",
        website: user.website || "",
        instagram: user.instagram || "",
        twitter: user.twitter || "",
        tiktok: user.tiktok || "",
        onlyfans: user.onlyfans || "",
      });
      setUsername(user.landing_page_slug || "");
    }
  }, [user]);

  // Check username availability with debounce
  const checkUsernameAvailability = useCallback(async (value) => {
    if (!value || value.length < 3) {
      setUsernameAvailable(null);
      setUsernameError(value && value.length < 3 ? "Username must be at least 3 characters" : "");
      return;
    }

    // Check if it's the same as current
    if (value === user?.landing_page_slug) {
      setUsernameAvailable(null);
      setUsernameError("");
      return;
    }

    setCheckingUsername(true);
    try {
      const response = await axios.get(`${API}/public/username/check/${value}`, {
        headers: getAuthHeaders()
      });
      setUsernameAvailable(response.data.available);
      setUsernameError(response.data.error || "");
    } catch (err) {
      setUsernameError("Failed to check username");
      setUsernameAvailable(false);
    } finally {
      setCheckingUsername(false);
    }
  }, [user?.landing_page_slug]);

  // Handle username input change with debounce
  const handleUsernameChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setUsername(value);
    setUsernameAvailable(null);
    setUsernameError("");

    // Clear existing timeout
    if (usernameCheckTimeout.current) {
      clearTimeout(usernameCheckTimeout.current);
    }

    // Set new timeout for debounced check
    usernameCheckTimeout.current = setTimeout(() => {
      checkUsernameAvailability(value);
    }, 500);
  };

  // Save username
  const handleSaveUsername = async () => {
    if (!username || username === user?.landing_page_slug) return;
    if (!usernameAvailable) return;

    setSavingUsername(true);
    try {
      const response = await axios.patch(`${API}/public/username`, 
        { username },
        { headers: getAuthHeaders() }
      );
      setMessage({ type: "success", text: "Username updated successfully!" });
      refreshUser();
      setUsernameAvailable(null);
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to update username" });
    } finally {
      setSavingUsername(false);
    }
  };

  // Handle payment success from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    
    if (payment === 'success' && sessionId) {
      // Check payment status and update credits
      const checkPaymentStatus = async () => {
        try {
          const response = await axios.get(
            `${API}/checkout/status/${sessionId}`,
            { headers: getAuthHeaders() }
          );
          
          if (response.data.payment_status === 'paid') {
            setMessage({ 
              type: 'success', 
              text: `Payment successful! ${response.data.credits_added || 'Credits'} added to your balance.` 
            });
            // Refresh user to get updated credits
            refreshUser();
            // Switch to Unlocked tab to show new balance
            setActiveTab('unlocked');
          }
        } catch (err) {
        }
        
        // Clean up URL
        window.history.replaceState({}, document.title, '/app/profile');
      };
      
      checkPaymentStatus();
    } else if (payment === 'cancelled') {
      setMessage({ type: 'error', text: 'Payment was cancelled.' });
      window.history.replaceState({}, document.title, '/app/profile');
    }
  }, [refreshUser]);

  // Load follower/following counts on mount
  useEffect(() => {
    if (!user?.id) return;
    
    const fetchFollowCounts = async () => {
      try {
        const response = await axios.get(`${API}/public/follow-counts/${user.id}`, {
          headers: getAuthHeaders()
        });
        setFollowCounts({
          followers: response.data.followers_count || 0,
          following: response.data.following_count || 0
        });
      } catch (err) {
      }
    };
    fetchFollowCounts();
  }, [user?.id]);

  // Load featured creators on mount (only for fans)
  useEffect(() => {
    // Skip if still loading or no user
    if (loading || !user?.id) return;
    // Skip if user is a creator
    if (user?.role === 'creator') return;
    
    const fetchFeaturedCreators = async () => {
      setLoadingFeatured(true);
      try {
        const response = await axios.get(`${API}/public/featured-creators`, {
          withCredentials: true
        });
        setFeaturedCreators(response.data.featured_creators || []);
      } catch (err) {
        console.error("Failed to fetch featured creators:", err);
      } finally {
        setLoadingFeatured(false);
      }
    };
    fetchFeaturedCreators();
  }, [loading, user?.id, user?.role]);

  // Fetch invite code if invite system is enabled
  useEffect(() => {
    if (loading || !user?.id) return;
    
    const fetchInviteData = async () => {
      try {
        // First check if invite is enabled
        const settingsResponse = await axios.get(`${API}/public/site-settings/invite`);
        setInviteEnabled(settingsResponse.data.enabled || false);
        
        if (settingsResponse.data.enabled) {
          // Fetch user's invite code
          const inviteResponse = await axios.get(`${API}/public/invite/my-code`, {
            withCredentials: true
          });
          setInviteCode(inviteResponse.data.invite_code);
          setInvitedCount(inviteResponse.data.invited_count || 0);
        }
      } catch (err) {
        console.error("Failed to fetch invite data:", err);
      }
    };
    fetchInviteData();
  }, [loading, user?.id]);

  // Fetch follower visibility setting
  useEffect(() => {
    const fetchFollowerVisibility = async () => {
      try {
        const response = await axios.get(`${API}/public/site-settings/follower-visibility`);
        setShowFollowerCounts(response.data.show_followers !== false);
      } catch (err) {
        // Default to showing if API fails
        setShowFollowerCounts(true);
      }
    };
    fetchFollowerVisibility();
  }, []);

  // Infinite scroll observer for content tab
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && contentHasMore && !loadingMoreContent && activeTab === 'content') {
          loadMoreContent();
        }
      },
      { threshold: 0.1 }
    );
    
    if (contentLoaderRef.current) {
      observer.observe(contentLoaderRef.current);
    }
    
    return () => observer.disconnect();
  }, [contentHasMore, loadingMoreContent, activeTab]);

  // Infinite scroll observer for unlocked content tab
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && unlockedHasMore && !loadingMoreUnlocked && activeTab === 'unlocked') {
          loadMoreUnlocked();
        }
      },
      { threshold: 0.1 }
    );
    
    if (unlockedLoaderRef.current) {
      observer.observe(unlockedLoaderRef.current);
    }
    
    return () => observer.disconnect();
  }, [unlockedHasMore, loadingMoreUnlocked, activeTab]);

  // ==================== UNLOCKED CONTENT LIGHTBOX ====================
  
  const totalUnlockedImages = unlockedContent.length;
  
  const goToPreviousUnlocked = useCallback(() => {
    if (selectedUnlockedIndex !== null && totalUnlockedImages > 0) {
      const newIndex = selectedUnlockedIndex === 0 ? totalUnlockedImages - 1 : selectedUnlockedIndex - 1;
      setLightboxImageLoading(true);
      setSelectedUnlockedIndex(newIndex);
      setZoomLevel(1); // Reset zoom on navigation
    }
  }, [selectedUnlockedIndex, totalUnlockedImages]);

  const goToNextUnlocked = useCallback(() => {
    if (selectedUnlockedIndex !== null && totalUnlockedImages > 0) {
      const newIndex = selectedUnlockedIndex === totalUnlockedImages - 1 ? 0 : selectedUnlockedIndex + 1;
      setLightboxImageLoading(true);
      setSelectedUnlockedIndex(newIndex);
      setZoomLevel(1); // Reset zoom on navigation
    }
  }, [selectedUnlockedIndex, totalUnlockedImages]);

  const closeLightbox = useCallback(() => {
    setSelectedUnlockedIndex(null);
    setZoomLevel(1);
    setLightboxImageLoading(false);
  }, []);

  const openLightbox = useCallback((index) => {
    setLightboxImageLoading(true);
    setSelectedUnlockedIndex(index);
  }, []);

  const zoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  }, []);

  const resetZoom = useCallback(() => {
    setZoomLevel(1);
  }, []);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedUnlockedIndex === null) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          goToPreviousUnlocked();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goToNextUnlocked();
          break;
        case 'Escape':
          e.preventDefault();
          closeLightbox();
          break;
        case '+':
        case '=':
          e.preventDefault();
          zoomIn();
          break;
        case '-':
          e.preventDefault();
          zoomOut();
          break;
        case '0':
          e.preventDefault();
          resetZoom();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedUnlockedIndex, goToPreviousUnlocked, goToNextUnlocked, closeLightbox, zoomIn, zoomOut, resetZoom]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (selectedUnlockedIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedUnlockedIndex]);

  // Redirect if not authenticated
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (!user) {
    navigate("/login");
    return null;
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });

    const result = await updateProfile(formData);

    if (result.success) {
      setMessage({ type: "success", text: "Profile updated successfully!" });
      setEditMode(false);
    } else {
      setMessage({ type: "error", text: result.error });
    }

    setSaving(false);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setMessage({ type: "", text: "" });
    const result = await uploadAvatar(file);

    if (result.success) {
      setMessage({ type: "success", text: "Avatar uploaded!" });
    } else {
      setMessage({ type: "error", text: result.error });
    }
  };

  const handleHeaderUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setMessage({ type: "", text: "" });
    const result = await uploadHeader(file);

    if (result.success) {
      setMessage({ type: "success", text: "Header uploaded!" });
    } else {
      setMessage({ type: "error", text: result.error });
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Content functions
  const loadContent = async (reset = true) => {
    if (reset) {
      setLoadingContent(true);
      setContentPage(0);
    } else {
      setLoadingMoreContent(true);
    }
    
    try {
      const skip = reset ? 0 : contentPage * CONTENT_PAGE_SIZE;
      const response = await axios.get(
        `${API}/public/content?limit=${CONTENT_PAGE_SIZE}&skip=${skip}`, 
        { headers: getAuthHeaders() }
      );
      
      const newContent = response.data.content || [];
      if (reset) {
        setContent(newContent);
        setContentPage(1);
      } else {
        setContent(prev => [...prev, ...newContent]);
        setContentPage(prev => prev + 1);
      }
      setContentHasMore(response.data.has_more || false);
    } catch (err) {
    }
    
    setLoadingContent(false);
    setLoadingMoreContent(false);
  };
  
  const loadMoreContent = () => {
    if (!loadingMoreContent && contentHasMore) {
      loadContent(false);
    }
  };

  // Upload modal functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const isImage = file.type.startsWith("image/");
      const isVideo = file.type.startsWith("video/");
      
      if (!isImage && !isVideo) {
        setMessage({ type: "error", text: "Please select an image or video file" });
        return;
      }
      
      // Different size limits for images vs videos
      const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
      const maxSizeText = isVideo ? "100MB" : "10MB";
      
      if (file.size > maxSize) {
        setMessage({ type: "error", text: `File too large. Maximum size is ${maxSizeText}` });
        return;
      }
      setUploadFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setUploadPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const openUploadModal = () => {
    setShowUploadModal(true);
    setUploadFile(null);
    setUploadPreview(null);
    setUploadTitle("");
    setUploadDescription("");
    setUploadCategory("free");
    setUploadCost(0);
    setCustomCost("");
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadPreview(null);
    setUploadTitle("");
    setUploadDescription("");
    setUploadCategory("free");
    setUploadCost(0);
    setCustomCost("");
  };

  const handleContentUpload = async () => {
    if (!uploadFile) {
      setMessage({ type: "error", text: "Please select a file to upload" });
      return;
    }

    setUploadingContent(true);
    setMessage({ type: "", text: "" });

    try {
      // Compress image before upload
      const compressedFile = await compressImage(uploadFile, compressionPresets.content);
      
      const formData = new FormData();
      formData.append("file", compressedFile);
      if (uploadTitle) formData.append("title", uploadTitle);
      if (uploadDescription) formData.append("description", uploadDescription);
      formData.append("category", uploadCategory);
      formData.append("cost", uploadCost.toString());

      await axios.post(`${API}/public/content`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });

      setMessage({ type: "success", text: "Content uploaded!" });
      closeUploadModal();
      loadContent();
      refreshUser();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Upload failed" });
    }

    setUploadingContent(false);
  };

  // Edit content functions
  const openEditContentModal = (item) => {
    setEditingContent(item);
    setEditContentForm({
      title: item.title || "",
      description: item.description || "",
      category: item.category || "free",
      cost: item.cost || 0
    });
    setShowEditContentModal(true);
  };

  const closeEditContentModal = () => {
    setShowEditContentModal(false);
    setEditingContent(null);
    setEditContentForm({ title: "", description: "", category: "free", cost: 0 });
  };

  const handleSaveContent = async () => {
    if (!editingContent) return;
    
    setSavingContent(true);
    try {
      await axios.patch(
        `${API}/public/content/${editingContent.id}`,
        editContentForm,
        { headers: getAuthHeaders() }
      );
      
      setMessage({ type: "success", text: "Content updated!" });
      closeEditContentModal();
      loadContent();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Update failed" });
    }
    setSavingContent(false);
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm("Are you sure you want to delete this content?")) return;

    try {
      await axios.delete(`${API}/public/content/${contentId}`, { headers: getAuthHeaders() });
      setMessage({ type: "success", text: "Content deleted!" });
      loadContent();
      refreshUser();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Delete failed" });
    }
  };

  // Creator request functions
  const loadCreatorStatus = async () => {
    setLoadingCreatorStatus(true);
    try {
      const response = await axios.get(`${API}/public/creator-request/status`, { headers: getAuthHeaders() });
      setCreatorStatus(response.data);
    } catch (err) {
    }
    setLoadingCreatorStatus(false);
  };

  const handleRequestCreator = async () => {
    setRequestingCreator(true);
    setMessage({ type: "", text: "" });

    try {
      await axios.post(
        `${API}/public/creator-request`,
        { message: creatorMessage },
        { headers: getAuthHeaders() }
      );
      setMessage({ type: "success", text: "Creator request submitted!" });
      loadCreatorStatus();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Request failed" });
    }

    setRequestingCreator(false);
  };

  // Load content/creator status when switching tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setMessage({ type: "", text: "" });
    if (tab === "content" && content.length === 0) {
      loadContent();
    }
    if (tab === "creator" && !creatorStatus) {
      loadCreatorStatus();
    }
    if (tab === "unlocked" && unlockedContent.length === 0) {
      loadUnlockedContent();
    }
    if (tab === "purchases" && purchases.length === 0) {
      loadPurchases();
    }
    if (tab === "social" && followers.length === 0 && following.length === 0) {
      loadSocialData();
    }
    if (tab === "earnings" && earnings.length === 0) {
      loadEarnings();
    }
  };

  // Load user's unlocked content
  const loadUnlockedContent = async (reset = true) => {
    if (reset) {
      setLoadingUnlocked(true);
      setUnlockedPage(0);
    } else {
      setLoadingMoreUnlocked(true);
    }
    
    try {
      const skip = reset ? 0 : unlockedPage * UNLOCKED_PAGE_SIZE;
      const response = await axios.get(
        `${API}/public/unlocked-content/details?limit=${UNLOCKED_PAGE_SIZE}&skip=${skip}`,
        { headers: getAuthHeaders() }
      );
      
      const newContent = response.data.unlocked_content || [];
      if (reset) {
        setUnlockedContent(newContent);
        setUnlockedPage(1);
      } else {
        setUnlockedContent(prev => [...prev, ...newContent]);
        setUnlockedPage(prev => prev + 1);
      }
      setUnlockedHasMore(response.data.has_more || false);
    } catch (err) {
    } finally {
      setLoadingUnlocked(false);
      setLoadingMoreUnlocked(false);
    }
  };
  
  const loadMoreUnlocked = () => {
    if (!loadingMoreUnlocked && unlockedHasMore) {
      loadUnlockedContent(false);
    }
  };

  // Load user's purchase transactions
  const loadPurchases = async () => {
    setLoadingPurchases(true);
    try {
      const response = await axios.get(`${API}/public/transactions`, {
        headers: getAuthHeaders(),
      });
      setPurchases(response.data.transactions || []);
      setPurchaseStats({
        totalCredits: response.data.total_credits_purchased || 0,
        totalSpent: response.data.total_amount_spent || 0
      });
    } catch (err) {
    } finally {
      setLoadingPurchases(false);
    }
  };

  // Social (Followers/Following) functions
  const loadSocialData = async () => {
    await Promise.all([loadFollowers(), loadFollowing(), loadFollowCounts()]);
  };

  const loadFollowCounts = async () => {
    if (!user?.id) return;
    try {
      const response = await axios.get(`${API}/public/follow-counts/${user.id}`, {
        headers: getAuthHeaders(),
      });
      setFollowCounts({
        followers: response.data.followers_count || 0,
        following: response.data.following_count || 0
      });
    } catch (err) {
    }
  };

  const loadFollowers = async () => {
    setLoadingFollowers(true);
    try {
      const response = await axios.get(`${API}/public/followers`, {
        headers: getAuthHeaders(),
      });
      setFollowers(response.data.followers || []);
      setFollowCounts(prev => ({ ...prev, followers: response.data.total || 0 }));
    } catch (err) {
    } finally {
      setLoadingFollowers(false);
    }
  };

  const loadFollowing = async () => {
    setLoadingFollowing(true);
    try {
      const response = await axios.get(`${API}/public/following`, {
        headers: getAuthHeaders(),
      });
      setFollowing(response.data.following || []);
      setFollowCounts(prev => ({ ...prev, following: response.data.total || 0 }));
    } catch (err) {
    } finally {
      setLoadingFollowing(false);
    }
  };

  const handleUnfollow = async (userId) => {
    try {
      await axios.delete(`${API}/public/follow/${userId}`, {
        headers: getAuthHeaders(),
      });
      // Remove from following list
      setFollowing(prev => prev.filter(u => u.id !== userId));
      setFollowCounts(prev => ({ ...prev, following: Math.max(0, prev.following - 1) }));
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to unfollow" });
    }
  };

  const handleFollowFromList = async (userId) => {
    try {
      await axios.post(`${API}/public/follow/${userId}`, {}, {
        headers: getAuthHeaders(),
      });
      // Update the is_following status in the list
      setFollowers(prev => prev.map(u => 
        u.id === userId ? { ...u, is_following: true } : u
      ));
      setFollowCounts(prev => ({ ...prev, following: prev.following + 1 }));
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Failed to follow" });
    }
  };

  // Load creator earnings
  const loadEarnings = async () => {
    setLoadingEarnings(true);
    try {
      const response = await axios.get(`${API}/public/creator/earnings`, {
        headers: getAuthHeaders(),
      });
      setEarnings(response.data.earnings || []);
      setEarningsStats({
        totalEarnings: response.data.total_earnings || 0,
        currentBalance: response.data.current_balance || 0,
        earningsCount: response.data.earnings_count || 0
      });
    } catch (err) {
    } finally {
      setLoadingEarnings(false);
    }
  };

  // Handle change password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess(false);
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/public/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      setPasswordSuccess(true);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setTimeout(() => {
        setPasswordSuccess(false);
        setShowChangePassword(false);
      }, 2000);
    } catch (err) {
      setPasswordError(err.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header Image */}
      <div className="relative h-48 md:h-64 bg-gradient-to-r from-cyan-600 to-purple-600">
        {user.header_image && (
          <img
            src={getImageSrc(user.header_image)}
            alt="Header"
            className="w-full h-full object-cover"
          />
        )}
        <button
          onClick={() => headerInputRef.current?.click()}
          className="absolute bottom-4 right-4 bg-black/50 hover:bg-black/70 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          data-testid="upload-header-button"
        >
          <Camera className="w-4 h-4" />
          Change Header
        </button>
        <input
          ref={headerInputRef}
          type="file"
          accept="image/*"
          onChange={handleHeaderUpload}
          className="hidden"
        />
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-16 relative z-10 pb-12">
        {/* Profile Header */}
        <div className="flex flex-col md:flex-row items-start md:items-end gap-4 mb-6">
          {/* Avatar */}
          <div className="relative">
            <div className="w-32 h-32 rounded-full border-4 border-slate-900 bg-slate-800 overflow-hidden">
              {user.profile_image ? (
                <img src={getImageSrc(user.profile_image)} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="w-12 h-12 text-gray-500" />
                </div>
              )}
            </div>
            <button
              onClick={() => avatarInputRef.current?.click()}
              className="absolute bottom-0 right-0 bg-cyan-500 hover:bg-cyan-400 text-white p-2 rounded-full transition-colors"
              data-testid="upload-avatar-button"
            >
              <Camera className="w-4 h-4" />
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
            />
          </div>

          {/* User Info */}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-white">{user.name}</h1>
              {user.role === "creator" && (
                <span className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  Creator
                </span>
              )}
            </div>
            <p className="text-gray-400">@{user.email.split("@")[0]}</p>
            
            {/* Stats Row: Posts, Followers, Following */}
            <div className="flex items-center gap-4 mt-2">
              <span className="text-gray-500 text-sm">{user.content_count || 0} posts</span>
              {showFollowerCounts && (
                <>
                  <button
                    onClick={() => {
                      loadSocialData();
                      setSocialTab("followers");
                      setShowSocialModal(true);
                    }}
                    className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer"
                    data-testid="followers-count-btn"
                  >
                    <span className="font-semibold text-white">{followCounts.followers}</span> followers
                  </button>
                  <button
                    onClick={() => {
                      loadSocialData();
                      setSocialTab("following");
                      setShowSocialModal(true);
                    }}
                    className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer"
                    data-testid="following-count-btn"
                  >
                    <span className="font-semibold text-white">{followCounts.following}</span> following
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Credits & Actions */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Credits Balance */}
            <div className="flex items-center gap-2 bg-amber-500/20 border border-amber-500/30 rounded-lg px-3 py-2">
              <Coins className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 font-bold">{user.credits || 0}</span>
              <span className="text-amber-400/70 text-sm">credits</span>
            </div>
            
            {/* Payment Info Button - Creators Only */}
            {user.role === "creator" && (
              <Link
                to="/app/payment-info"
                className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/30 px-3 py-2 rounded-lg flex items-center gap-2 transition-colors"
                data-testid="payment-info-btn"
              >
                <Banknote className="w-4 h-4" />
                <span className="hidden sm:inline">Payment Info</span>
              </Link>
            )}
            
            {/* Buy Credits Button - Fans Only */}
            {user.role !== "creator" && (
              <button
                onClick={() => setShowBuyCreditsModal(true)}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:opacity-90 text-black font-semibold px-3 py-2 rounded-lg flex items-center gap-2 transition-opacity"
                data-testid="header-buy-credits-btn"
              >
                <ShoppingCart className="w-4 h-4" />
                Buy Credits
              </button>
            )}
            
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
              message.type === "success"
                ? "bg-green-500/20 border border-green-500/50 text-green-300"
                : "bg-red-500/20 border border-red-500/50 text-red-300"
            }`}
          >
            {message.type === "success" ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            {message.text}
          </motion.div>
        )}

        {/* Featured Creators Section - Only show for fans */}
        {user?.role !== "creator" && featuredCreators.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-gradient-to-r from-amber-500/10 to-orange-500/10 backdrop-blur-xl rounded-xl p-4 border border-amber-500/20"
            data-testid="featured-creators-section"
          >
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-5 h-5 text-amber-400" />
              <h3 className="text-sm font-semibold text-amber-300">Featured Creators</h3>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {featuredCreators.map((creator) => (
                <Link
                  key={creator.id}
                  to={`/u/${creator.landing_page_slug}`}
                  className="flex flex-col items-center gap-2 p-3 rounded-lg bg-black/20 hover:bg-black/40 transition-colors group"
                  data-testid={`featured-creator-${creator.id}`}
                >
                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-amber-500/50 group-hover:border-amber-400 transition-colors bg-gray-800">
                    {creator.profile_image ? (
                      <img
                        src={creator.profile_image.startsWith("data:") ? creator.profile_image : `data:image/jpeg;base64,${creator.profile_image}`}
                        alt={creator.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-500/20 to-orange-500/20">
                        <User className="w-7 h-7 text-amber-400" />
                      </div>
                    )}
                  </div>
                  <span className="text-sm text-gray-300 group-hover:text-amber-300 transition-colors text-center truncate w-full font-medium">
                    {creator.name}
                  </span>
                </Link>
              ))}
            </div>
          </motion.div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-white/5 rounded-lg p-1 mb-6 overflow-x-auto">
          {["profile", "content", "unlocked", "purchases", ...(user?.role === "creator" ? ["earnings"] : []), "creator"].map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? "bg-gradient-to-r from-cyan-500 to-purple-600 text-white"
                  : "text-gray-400 hover:text-white"
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab === "unlocked" ? "Unlocked" : tab === "purchases" ? "Purchases" : tab === "earnings" ? "Earnings" : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-white">Profile Information</h2>
              {!editMode ? (
                <button
                  onClick={() => {
                    setEditMode(true);
                    setFormData({
                      name: user.name || "",
                      bio: user.bio || "",
                      location: user.location || "",
                      website: user.website || "",
                      instagram: user.instagram || "",
                      twitter: user.twitter || "",
                      tiktok: user.tiktok || "",
                      onlyfans: user.onlyfans || "",
                    });
                  }}
                  className="text-cyan-400 hover:text-cyan-300 transition-colors"
                  data-testid="edit-profile-button"
                >
                  Edit Profile
                </button>
              ) : (
                <button
                  onClick={() => setEditMode(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-gray-400 text-sm mb-1">Name</label>
                {editMode ? (
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500"
                    data-testid="profile-name-input"
                  />
                ) : (
                  <p className="text-white">{user.name || "-"}</p>
                )}
              </div>

              {/* Bio */}
              <div>
                <label className="block text-gray-400 text-sm mb-1">Bio</label>
                {editMode ? (
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    rows={3}
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500 resize-none"
                    data-testid="profile-bio-input"
                  />
                ) : (
                  <p className="text-white">{user.bio || "-"}</p>
                )}
              </div>

              {/* Username/Slug */}
              <div>
                <label className="block text-gray-400 text-sm mb-1 flex items-center gap-1">
                  <AtSign className="w-4 h-4" /> Username
                </label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={username}
                        onChange={handleUsernameChange}
                        placeholder="your_username"
                        className={`w-full bg-white/5 border rounded-lg py-2 px-3 text-white focus:outline-none transition-colors ${
                          usernameError 
                            ? 'border-red-500 focus:border-red-500' 
                            : usernameAvailable === true 
                              ? 'border-green-500 focus:border-green-500' 
                              : 'border-white/10 focus:border-cyan-500'
                        }`}
                        data-testid="profile-username-input"
                      />
                      {checkingUsername && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                      )}
                      {!checkingUsername && usernameAvailable === true && (
                        <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                      )}
                      {!checkingUsername && usernameAvailable === false && (
                        <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                      )}
                    </div>
                    <button
                      onClick={handleSaveUsername}
                      disabled={!usernameAvailable || savingUsername || username === user?.landing_page_slug}
                      className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                      data-testid="save-username-btn"
                    >
                      {savingUsername ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Save
                    </button>
                  </div>
                  {usernameError && (
                    <p className="text-red-400 text-xs">{usernameError}</p>
                  )}
                  {usernameAvailable === true && (
                    <p className="text-green-400 text-xs">Username is available!</p>
                  )}
                  {user?.landing_page_slug && (
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>Your profile URL:</span>
                      <a 
                        href={`/u/${user.landing_page_slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                      >
                        /u/{user.landing_page_slug}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-gray-400 text-sm mb-1 flex items-center gap-1">
                  <MapPin className="w-4 h-4" /> Location
                </label>
                {editMode ? (
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500"
                    data-testid="profile-location-input"
                  />
                ) : (
                  <p className="text-white">{user.location || "-"}</p>
                )}
              </div>

              {/* Website */}
              <div>
                <label className="block text-gray-400 text-sm mb-1 flex items-center gap-1">
                  <Globe className="w-4 h-4" /> Website
                </label>
                {editMode ? (
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500"
                    data-testid="profile-website-input"
                  />
                ) : (
                  <p className="text-white">{user.website || "-"}</p>
                )}
              </div>

              {/* Social Links */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1 flex items-center gap-1">
                    <Instagram className="w-4 h-4" /> Instagram
                  </label>
                  {editMode ? (
                    <input
                      type="text"
                      name="instagram"
                      value={formData.instagram}
                      onChange={handleChange}
                      placeholder="@username"
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500"
                    />
                  ) : (
                    <p className="text-white">{user.instagram || "-"}</p>
                  )}
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1 flex items-center gap-1">
                    <Twitter className="w-4 h-4" /> Twitter
                  </label>
                  {editMode ? (
                    <input
                      type="text"
                      name="twitter"
                      value={formData.twitter}
                      onChange={handleChange}
                      placeholder="@username"
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500"
                    />
                  ) : (
                    <p className="text-white">{user.twitter || "-"}</p>
                  )}
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">TikTok</label>
                  {editMode ? (
                    <input
                      type="text"
                      name="tiktok"
                      value={formData.tiktok}
                      onChange={handleChange}
                      placeholder="@username"
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500"
                    />
                  ) : (
                    <p className="text-white">{user.tiktok || "-"}</p>
                  )}
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">OnlyFans</label>
                  {editMode ? (
                    <input
                      type="text"
                      name="onlyfans"
                      value={formData.onlyfans}
                      onChange={handleChange}
                      placeholder="username"
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500"
                    />
                  ) : (
                    <p className="text-white">{user.onlyfans || "-"}</p>
                  )}
                </div>
              </div>

              {/* Save Button */}
              {editMode && (
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="w-full bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-semibold py-3 rounded-lg hover:from-cyan-400 hover:to-purple-500 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="save-profile-button"
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Save className="w-5 h-5" />
                  )}
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              )}
            </div>
          </motion.div>
        )}

        {/* Change Password Section */}
        {activeTab === "profile" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10"
            data-testid="change-password-section"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Lock className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Password & Security</h3>
                  <p className="text-sm text-gray-400">Update your password</p>
                </div>
              </div>
              {!showChangePassword && (
                <button
                  onClick={() => setShowChangePassword(true)}
                  className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors text-sm font-medium"
                  data-testid="change-password-btn"
                >
                  Change Password
                </button>
              )}
            </div>

            {showChangePassword && (
              <form onSubmit={handleChangePassword} className="space-y-4">
                {passwordError && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                    <AlertCircle size={16} />
                    {passwordError}
                  </div>
                )}
                {passwordSuccess && (
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2 text-green-400 text-sm">
                    <CheckCircle size={16} />
                    Password changed successfully!
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Current Password</label>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all"
                    placeholder="Enter current password"
                    required
                    data-testid="current-password-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">New Password</label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all"
                    placeholder="Enter new password (min. 8 characters)"
                    required
                    minLength={8}
                    data-testid="new-password-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all"
                    placeholder="Confirm new password"
                    required
                    minLength={8}
                    data-testid="confirm-password-input"
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowChangePassword(false);
                      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
                      setPasswordError("");
                    }}
                    className="flex-1 px-4 py-3 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={changingPassword}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="submit-password-btn"
                  >
                    {changingPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Changing...
                      </>
                    ) : (
                      "Update Password"
                    )}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        )}

        {/* Invite Friends Section - Show when invite is enabled */}
        {inviteEnabled && inviteCode && activeTab === "profile" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 bg-gradient-to-r from-green-500/10 to-emerald-500/10 backdrop-blur-xl rounded-xl p-6 border border-green-500/20"
            data-testid="invite-section"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <Gift className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Invite Friends</h3>
                <p className="text-sm text-gray-400">Share your link and earn rewards!</p>
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 rounded-full">
                <Users className="w-4 h-4 text-green-400" />
                <span className="text-green-300 text-sm font-medium">{invitedCount} invited</span>
              </div>
            </div>

            {/* Invite Link */}
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-black/30 rounded-lg px-4 py-3 border border-green-500/30">
                <p className="text-green-300 text-sm font-mono truncate">
                  {window.location.origin}/register?ref={inviteCode}
                </p>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/register?ref=${inviteCode}`);
                  setInviteCopied(true);
                  setTimeout(() => setInviteCopied(false), 2000);
                }}
                className={`px-4 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  inviteCopied 
                    ? "bg-green-500 text-white" 
                    : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                }`}
                data-testid="copy-invite-link-btn"
              >
                {inviteCopied ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>

            {/* Share buttons */}
            <div className="flex items-center gap-2 mt-4">
              <span className="text-gray-500 text-sm">Share:</span>
              <button
                onClick={() => window.open(`https://twitter.com/intent/tweet?text=Join%20me%20on%20HotPeek!&url=${encodeURIComponent(`${window.location.origin}/register?ref=${inviteCode}`)}`, '_blank')}
                className="p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                title="Share on Twitter"
              >
                <Twitter className="w-4 h-4 text-gray-400" />
              </button>
              <button
                onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${window.location.origin}/register?ref=${inviteCode}`)}`, '_blank')}
                className="p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                title="Share on Facebook"
              >
                <Share2 className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </motion.div>
        )}

        {/* Content Tab */}
        {activeTab === "content" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Upload Button */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white">My Content</h2>
                <span className="text-gray-400">{content.length} items</span>
              </div>
              
              <button
                onClick={openUploadModal}
                className="w-full border-2 border-dashed border-white/20 hover:border-cyan-500 rounded-xl p-8 flex flex-col items-center justify-center gap-2 transition-colors"
                data-testid="upload-content-button"
              >
                <Upload className="w-8 h-8 text-gray-400" />
                <span className="text-gray-400">Click to upload content</span>
                <span className="text-gray-500 text-sm">PNG, JPG, GIF up to 10MB</span>
              </button>
            </div>

            {/* Content Grid */}
            {loadingContent ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
              </div>
            ) : content.length > 0 ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {content.map((item) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="relative group aspect-square bg-slate-800 rounded-xl overflow-hidden"
                    >
                      {item.media_type === "video" || item.image_data?.startsWith("data:video/") ? (
                        <video
                          src={item.image_data}
                          className="w-full h-full object-cover"
                          muted
                          loop
                          onMouseOver={(e) => e.target.play()}
                          onMouseOut={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                        />
                      ) : (
                        <img
                          src={item.image_data}
                          alt={item.title || "Content"}
                          className="w-full h-full object-cover"
                        />
                      )}
                      {/* Badges - Top Left (Video, Category, Cost) */}
                      <div className="absolute top-2 left-2 flex flex-col gap-1 z-10">
                        {/* Video indicator */}
                        {(item.media_type === "video" || item.image_data?.startsWith("data:video/")) && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/90 text-white flex items-center gap-1">
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                            Video
                          </span>
                        )}
                        {/* Category badge */}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.category === "premium" 
                            ? "bg-amber-500/90 text-black" 
                            : "bg-green-500/90 text-white"
                        }`}>
                          {item.category === "premium" ? "Premium" : "Free"}
                        </span>
                        {/* Cost badge */}
                        {item.cost > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-500/90 text-white flex items-center gap-1">
                            <Coins className="w-3 h-3" />
                            {item.cost}
                          </span>
                        )}
                      </div>
                      {/* Title - Bottom */}
                      {item.title && (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 pt-6 z-10">
                          <p className="text-white text-xs font-medium truncate">{item.title}</p>
                        </div>
                      )}
                      {/* Hover overlay with edit and delete buttons */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button
                          onClick={() => openEditContentModal(item)}
                          className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition-colors"
                          title="Edit content"
                          data-testid={`edit-content-${item.id}`}
                        >
                          <Pencil className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteContent(item.id)}
                          className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors"
                          title="Delete content"
                          data-testid={`delete-content-${item.id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                {/* Infinite scroll loader / Load More button */}
                <div ref={contentLoaderRef} className="flex flex-col items-center gap-4 py-4">
                  {loadingMoreContent && (
                    <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
                  )}
                  {contentHasMore && !loadingMoreContent && content.length >= CONTENT_PAGE_SIZE && (
                    <button
                      onClick={loadMoreContent}
                      className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                      data-testid="load-more-content-btn"
                    >
                      Load More Content
                    </button>
                  )}
                  {!contentHasMore && content.length > CONTENT_PAGE_SIZE && (
                    <p className="text-gray-500 text-sm">No more content to load</p>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <ImagePlus className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No content yet. Upload your first image!</p>
              </div>
            )}
          </motion.div>
        )}

        {/* Unlocked Tab */}
        {activeTab === "unlocked" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Credits Balance Card */}
            <div className="bg-gradient-to-r from-amber-500/10 to-purple-500/10 border border-amber-500/20 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Coins className="w-7 h-7 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-white text-lg font-semibold">Your Credit Balance</p>
                    <p className="text-gray-400 text-sm">Use credits to unlock premium content from creators</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-4xl font-bold text-amber-400">{user.credits || 0}</p>
                    <p className="text-gray-500 text-sm">credits available</p>
                  </div>
                  <button
                    onClick={() => setShowBuyCreditsModal(true)}
                    className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2"
                    data-testid="buy-credits-btn"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Buy Credits
                  </button>
                </div>
              </div>
            </div>

            {/* Unlocked Content Grid */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <Unlock className="w-5 h-5 text-green-400" />
                My Unlocked Content
                <span className="text-gray-500 font-normal">({unlockedContent.length} items)</span>
              </h2>

              {loadingUnlocked ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
                </div>
              ) : unlockedContent.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {unlockedContent.map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="relative group aspect-square bg-slate-800 rounded-xl overflow-hidden cursor-pointer"
                        onClick={() => openLightbox(index)}
                        data-testid={`unlocked-item-${index}`}
                      >
                        {/* Video or Image */}
                        {item.media_type === "video" || item.image_data?.startsWith("data:video/") ? (
                          <video
                            src={item.image_data}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            muted
                            loop
                            playsInline
                            onMouseOver={(e) => e.target.play()}
                            onMouseOut={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                          />
                        ) : (
                          <img
                            src={item.image_data}
                            alt={item.title || "Unlocked content"}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        )}
                        {/* Badges - Top Left */}
                        <div className="absolute top-2 left-2 flex flex-col gap-1 z-10">
                          {/* Video indicator */}
                          {(item.media_type === "video" || item.image_data?.startsWith("data:video/")) && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/90 text-white flex items-center gap-1">
                              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                              Video
                            </span>
                          )}
                          {/* Unlocked badge */}
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/90 text-white flex items-center gap-1">
                            <Unlock className="w-3 h-3" />
                            Unlocked
                          </span>
                        </div>
                        {/* Title - Bottom (always visible) */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 pt-6">
                          <p className="text-white text-xs font-medium truncate">{item.title || "Untitled"}</p>
                          <div className="flex items-center justify-between mt-0.5">
                            <span className="text-gray-400 text-xs truncate">by {item.creator_name}</span>
                            <span className="text-amber-400 text-xs flex items-center gap-1">
                              <Coins className="w-3 h-3" />
                              {item.cost_paid}
                            </span>
                          </div>
                        </div>
                        {/* View indicator on hover */}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          <div className="bg-black/50 rounded-full p-3">
                            <ZoomIn className="w-6 h-6 text-white" />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  
                  {/* Load More / Infinite scroll loader */}
                  <div ref={unlockedLoaderRef} className="flex flex-col items-center gap-3 py-4 mt-4">
                    {loadingMoreUnlocked && (
                      <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
                    )}
                    {unlockedHasMore && !loadingMoreUnlocked && (
                      <button
                        onClick={loadMoreUnlocked}
                        className="px-6 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Load More
                      </button>
                    )}
                    {!unlockedHasMore && unlockedContent.length > UNLOCKED_PAGE_SIZE && (
                      <p className="text-gray-500 text-sm">No more content to load</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-gray-500" />
                  </div>
                  <p className="text-gray-400 mb-2">No unlocked content yet</p>
                  <p className="text-gray-500 text-sm">
                    Browse creator profiles and unlock premium content with your credits!
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Purchases Tab */}
        {activeTab === "purchases" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Purchase Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Total Spent</p>
                    <p className="text-2xl font-bold text-emerald-400">
                      ${purchaseStats.totalSpent.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Coins className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Credits Purchased</p>
                    <p className="text-2xl font-bold text-amber-400">
                      {purchaseStats.totalCredits.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Transactions</p>
                    <p className="text-2xl font-bold text-cyan-400">
                      {purchases.length}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Transactions List */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Receipt className="w-5 h-5 text-cyan-400" />
                Purchase History
              </h2>

              {loadingPurchases ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
                </div>
              ) : purchases.length > 0 ? (
                <div className="space-y-3">
                  {purchases.map((purchase) => (
                    <div
                      key={purchase.id}
                      className="bg-slate-800/50 rounded-lg p-4 border border-slate-700 flex items-center justify-between"
                      data-testid={`purchase-${purchase.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          purchase.payment_status === "paid" 
                            ? "bg-emerald-500/20" 
                            : purchase.payment_status === "pending" || purchase.payment_status === "initiated"
                            ? "bg-yellow-500/20"
                            : "bg-red-500/20"
                        }`}>
                          {purchase.payment_status === "paid" ? (
                            <CheckCircle className="w-5 h-5 text-emerald-400" />
                          ) : purchase.payment_status === "pending" || purchase.payment_status === "initiated" ? (
                            <Clock className="w-5 h-5 text-yellow-400" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {purchase.credits?.toLocaleString()} Credits
                          </p>
                          <div className="flex items-center gap-2 text-sm text-gray-400">
                            <Calendar className="w-3 h-3" />
                            {new Date(purchase.created_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-emerald-400">
                          ${purchase.amount?.toFixed(2)}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          purchase.payment_status === "paid"
                            ? "bg-emerald-500/20 text-emerald-400"
                            : purchase.payment_status === "pending" || purchase.payment_status === "initiated"
                            ? "bg-yellow-500/20 text-yellow-400"
                            : "bg-red-500/20 text-red-400"
                        }`}>
                          {purchase.payment_status?.charAt(0).toUpperCase() + purchase.payment_status?.slice(1)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Receipt className="w-8 h-8 text-gray-500" />
                  </div>
                  <p className="text-gray-400 mb-2">No purchases yet</p>
                  <p className="text-gray-500 text-sm mb-4">
                    Buy credits to unlock premium content from creators!
                  </p>
                  <button
                    onClick={() => setShowBuyCreditsModal(true)}
                    className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold rounded-lg hover:opacity-90 transition-opacity inline-flex items-center gap-2"
                    data-testid="buy-credits-empty"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Buy Credits
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Earnings Tab (Creators Only) */}
        {activeTab === "earnings" && user?.role === "creator" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Earnings Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/20 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Total Earned</p>
                    <p className="text-2xl font-bold text-emerald-400">
                      {earningsStats.totalEarnings.toLocaleString()} credits
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Current Balance</p>
                    <p className="text-2xl font-bold text-amber-400">
                      {earningsStats.currentBalance.toLocaleString()} credits
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Unlock className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Content Unlocks</p>
                    <p className="text-2xl font-bold text-cyan-400">
                      {earningsStats.earningsCount}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Earnings History */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                Earnings History
              </h2>

              {loadingEarnings ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
                </div>
              ) : earnings.length > 0 ? (
                <div className="space-y-3">
                  {earnings.map((earning) => (
                    <div
                      key={earning.id}
                      className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                      data-testid={`earning-${earning.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                          <Coins className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {earning.content_title || "Content Unlock"}
                          </p>
                          <p className="text-gray-400 text-sm">
                            Unlocked by {earning.fan_name}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(earning.created_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-emerald-400 flex items-center gap-1">
                          +{earning.amount}
                          <Coins className="w-4 h-4" />
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-8 h-8 text-gray-500" />
                  </div>
                  <p className="text-gray-400 mb-2">No earnings yet</p>
                  <p className="text-gray-500 text-sm">
                    Upload premium content and fans will unlock it to earn credits!
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Creator Tab */}
        {activeTab === "creator" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10"
          >
            <h2 className="text-xl font-semibold text-white mb-4">Become a Creator</h2>

            {user.role === "creator" ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">You're a Creator!</h3>
                <p className="text-gray-400">
                  Congratulations! You have full creator privileges on HotPeek.
                </p>
              </div>
            ) : loadingCreatorStatus ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
              </div>
            ) : creatorStatus?.has_request ? (
              <div className="text-center py-8">
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                    creatorStatus.request.status === "pending"
                      ? "bg-yellow-500/20"
                      : creatorStatus.request.status === "approved"
                      ? "bg-green-500/20"
                      : "bg-red-500/20"
                  }`}
                >
                  {creatorStatus.request.status === "pending" ? (
                    <Loader2 className="w-8 h-8 text-yellow-400 animate-spin" />
                  ) : creatorStatus.request.status === "approved" ? (
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  ) : (
                    <X className="w-8 h-8 text-red-400" />
                  )}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  Request {creatorStatus.request.status.charAt(0).toUpperCase() + creatorStatus.request.status.slice(1)}
                </h3>
                <p className="text-gray-400">
                  {creatorStatus.request.status === "pending"
                    ? "Your request is being reviewed by our team."
                    : creatorStatus.request.status === "approved"
                    ? "Your request was approved! Please refresh the page."
                    : "Your request was rejected. You can try again later."}
                </p>
                <p className="text-gray-500 text-sm mt-2">
                  Submitted: {new Date(creatorStatus.request.created_at).toLocaleDateString()}
                </p>
              </div>
            ) : (
              <>
                {/* Requirements */}
                <div className="mb-6 space-y-3">
                  <p className="text-gray-300">To become a creator, you need:</p>
                  <div className="space-y-2">
                    <div
                      className={`flex items-center gap-2 ${
                        creatorStatus?.requirements?.has_profile_image
                          ? "text-green-400"
                          : "text-gray-400"
                      }`}
                    >
                      {creatorStatus?.requirements?.has_profile_image ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <div className="w-5 h-5 border-2 border-current rounded-full" />
                      )}
                      Profile image uploaded
                    </div>
                    <div
                      className={`flex items-center gap-2 ${
                        (creatorStatus?.requirements?.content_count || 0) >= 5
                          ? "text-green-400"
                          : "text-gray-400"
                      }`}
                    >
                      {(creatorStatus?.requirements?.content_count || 0) >= 5 ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <div className="w-5 h-5 border-2 border-current rounded-full" />
                      )}
                      At least 5 content images ({creatorStatus?.requirements?.content_count || 0}/5)
                    </div>
                  </div>
                </div>

                {/* Request Form */}
                {creatorStatus?.can_request ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">
                        Message (optional)
                      </label>
                      <textarea
                        value={creatorMessage}
                        onChange={(e) => setCreatorMessage(e.target.value)}
                        rows={3}
                        placeholder="Tell us why you want to become a creator..."
                        className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500 resize-none"
                        data-testid="creator-message-input"
                      />
                    </div>
                    <button
                      onClick={handleRequestCreator}
                      disabled={requestingCreator}
                      className="w-full bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-semibold py-3 rounded-lg hover:from-cyan-400 hover:to-purple-500 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="request-creator-button"
                    >
                      {requestingCreator ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Star className="w-5 h-5" />
                      )}
                      {requestingCreator ? "Submitting..." : "Request Creator Status"}
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-400 text-center">
                    Complete the requirements above to request creator status.
                  </p>
                )}
              </>
            )}
          </motion.div>
        )}
      </div>

      {/* Upload Content Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto"
            onClick={closeUploadModal}
          >
            <div className="min-h-full flex items-start justify-center py-8 px-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-slate-800 rounded-2xl w-full max-w-md border border-white/10 shadow-2xl"
              >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <h3 className="text-xl font-semibold text-white">Upload Content</h3>
                <button
                  onClick={closeUploadModal}
                  className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-4">
                {/* File Upload Area */}
                <div
                  onClick={() => contentInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                    uploadPreview 
                      ? "border-cyan-500/50 bg-cyan-500/5" 
                      : "border-white/20 hover:border-white/40 hover:bg-white/5"
                  }`}
                >
                  <input
                    ref={contentInputRef}
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {uploadPreview ? (
                    <div className="space-y-3">
                      {uploadFile?.type?.startsWith("video/") ? (
                        <video
                          src={uploadPreview}
                          className="max-h-48 mx-auto rounded-lg object-contain"
                          controls
                        />
                      ) : (
                        <img
                          src={uploadPreview}
                          alt="Preview"
                          className="max-h-48 mx-auto rounded-lg object-contain"
                        />
                      )}
                      <p className="text-sm text-gray-400">Click to change file</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="w-16 h-16 mx-auto rounded-full bg-white/10 flex items-center justify-center">
                        <Plus className="w-8 h-8 text-gray-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Click to select an image or video</p>
                        <p className="text-sm text-gray-500">Images up to 10MB, Videos up to 100MB</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Title Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Title (optional)</label>
                  <input
                    type="text"
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    placeholder="Enter a title for this content"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all"
                  />
                </div>

                {/* Description Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description (optional)</label>
                  <textarea
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    placeholder="Enter a description"
                    rows={2}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all resize-none"
                  />
                </div>

                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setUploadCategory("free")}
                      className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                        uploadCategory === "free"
                          ? "bg-green-500/20 text-green-400 border-2 border-green-500"
                          : "bg-white/5 text-gray-400 border border-white/10 hover:border-white/20"
                      }`}
                    >
                      Free
                    </button>
                    <button
                      type="button"
                      onClick={() => setUploadCategory("premium")}
                      className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                        uploadCategory === "premium"
                          ? "bg-amber-500/20 text-amber-400 border-2 border-amber-500"
                          : "bg-white/5 text-gray-400 border border-white/10 hover:border-white/20"
                      }`}
                    >
                      Premium
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Free content is visible to registered fans. Premium requires payment.
                  </p>
                </div>

                {/* Cost Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <div className="flex items-center gap-2">
                      <Coins className="w-4 h-4 text-amber-400" />
                      Credit Cost
                    </div>
                  </label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {costPresets.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => {
                          setUploadCost(preset);
                          setCustomCost("");
                        }}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          uploadCost === preset && customCost === ""
                            ? "bg-amber-500/20 text-amber-400 border-2 border-amber-500"
                            : "bg-white/5 text-gray-400 border border-white/10 hover:border-white/20"
                        }`}
                      >
                        {preset === 0 ? "Free" : preset}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-sm">Custom:</span>
                    <input
                      type="number"
                      min="0"
                      value={customCost}
                      onChange={(e) => {
                        const val = e.target.value;
                        setCustomCost(val);
                        if (val !== "") {
                          setUploadCost(parseInt(val) || 0);
                        }
                      }}
                      placeholder="Enter custom credits"
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Set the credit cost to unlock this content. Use 0 for free content.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={closeUploadModal}
                    className="flex-1 py-3 border border-white/20 text-gray-300 font-medium rounded-lg hover:bg-white/5 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleContentUpload}
                    disabled={!uploadFile || uploadingContent}
                    className="flex-1 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {uploadingContent ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Upload
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Content Modal */}
      <AnimatePresence>
        {showEditContentModal && editingContent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto"
            onClick={closeEditContentModal}
          >
            <div className="min-h-full flex items-start justify-center py-8 px-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-slate-800 rounded-2xl w-full max-w-md border border-white/10 shadow-2xl"
              >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <h3 className="text-xl font-semibold text-white">Edit Content</h3>
                <button
                  onClick={closeEditContentModal}
                  className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-4">
                {/* Image Preview */}
                {editingContent.image_data && (
                  <div className="rounded-xl overflow-hidden">
                    <img
                      src={editingContent.image_data}
                      alt={editingContent.title || "Content"}
                      className="w-full max-h-48 object-contain bg-black/20"
                    />
                  </div>
                )}

                {/* Title Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                  <input
                    type="text"
                    value={editContentForm.title}
                    onChange={(e) => setEditContentForm({ ...editContentForm, title: e.target.value })}
                    placeholder="Enter a title"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all"
                  />
                </div>

                {/* Description Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                  <textarea
                    value={editContentForm.description}
                    onChange={(e) => setEditContentForm({ ...editContentForm, description: e.target.value })}
                    placeholder="Enter a description"
                    rows={2}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all resize-none"
                  />
                </div>

                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setEditContentForm({ ...editContentForm, category: "free" })}
                      className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                        editContentForm.category === "free"
                          ? "bg-green-500/20 text-green-400 border-2 border-green-500"
                          : "bg-white/5 text-gray-400 border border-white/10 hover:border-white/20"
                      }`}
                    >
                      Free
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditContentForm({ ...editContentForm, category: "premium" })}
                      className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                        editContentForm.category === "premium"
                          ? "bg-amber-500/20 text-amber-400 border-2 border-amber-500"
                          : "bg-white/5 text-gray-400 border border-white/10 hover:border-white/20"
                      }`}
                    >
                      Premium
                    </button>
                  </div>
                </div>

                {/* Cost Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <div className="flex items-center gap-2">
                      <Coins className="w-4 h-4 text-amber-400" />
                      Credit Cost
                    </div>
                  </label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {costPresets.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setEditContentForm({ ...editContentForm, cost: preset })}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          editContentForm.cost === preset
                            ? "bg-amber-500/20 text-amber-400 border-2 border-amber-500"
                            : "bg-white/5 text-gray-400 border border-white/10 hover:border-white/20"
                        }`}
                      >
                        {preset === 0 ? "Free" : preset}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-sm">Custom:</span>
                    <input
                      type="number"
                      min="0"
                      value={editContentForm.cost}
                      onChange={(e) => setEditContentForm({ ...editContentForm, cost: parseInt(e.target.value) || 0 })}
                      placeholder="Custom"
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-500"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={closeEditContentModal}
                    className="flex-1 py-3 border border-white/20 text-gray-300 font-medium rounded-lg hover:bg-white/5 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveContent}
                    disabled={savingContent}
                    className="flex-1 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {savingContent ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Unlocked Content Lightbox Gallery */}
      <AnimatePresence>
        {selectedUnlockedIndex !== null && unlockedContent.length > 0 && (
          <>
            {/* Solid black backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[99] bg-black"
              onClick={closeLightbox}
            />
            {/* Lightbox content */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex flex-col bg-black"
              onClick={closeLightbox}
              data-testid="unlocked-lightbox"
            >
            {/* Top Bar */}
            <div className="flex items-center justify-between p-4 text-white">
              <div className="flex items-center gap-4">
                <span className="text-lg font-medium">
                  {selectedUnlockedIndex + 1} / {totalUnlockedImages}
                </span>
                {unlockedContent[selectedUnlockedIndex]?.title && (
                  <span className="text-gray-400 hidden sm:inline">
                    {unlockedContent[selectedUnlockedIndex].title}
                  </span>
                )}
              </div>
              
              {/* Zoom Controls */}
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    zoomOut();
                  }}
                  disabled={zoomLevel <= MIN_ZOOM}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Zoom out (-)"
                  data-testid="lightbox-zoom-out"
                >
                  <ZoomOut size={20} />
                </button>
                <span className="text-sm min-w-[3rem] text-center">{Math.round(zoomLevel * 100)}%</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    zoomIn();
                  }}
                  disabled={zoomLevel >= MAX_ZOOM}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Zoom in (+)"
                  data-testid="lightbox-zoom-in"
                >
                  <ZoomIn size={20} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    resetZoom();
                  }}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                  title="Reset zoom (0)"
                  data-testid="lightbox-zoom-reset"
                >
                  <RotateCcw size={20} />
                </button>
                <div className="w-px h-6 bg-white/20 mx-2" />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    closeLightbox();
                  }}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                  title="Close (Esc)"
                  data-testid="lightbox-close"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Main Image Area */}
            <div className="flex-1 flex items-center justify-center relative px-4 sm:px-16 overflow-hidden">
              {/* Previous Button */}
              {totalUnlockedImages > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goToPreviousUnlocked();
                  }}
                  className="absolute left-2 sm:left-4 p-2 sm:p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
                  title="Previous (←)"
                  data-testid="lightbox-prev"
                >
                  <ChevronLeft size={28} className="text-white" />
                </button>
              )}

              {/* Image/Video with Zoom */}
              <motion.div
                key={selectedUnlockedIndex}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="flex items-center justify-center overflow-auto max-w-full max-h-full relative"
                onClick={(e) => e.stopPropagation()}
                style={{ cursor: zoomLevel > 1 ? 'move' : 'default' }}
              >
                {lightboxImageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="w-10 h-10 text-cyan-400 animate-spin" />
                  </div>
                )}
                {/* Check if it's a video */}
                {unlockedContent[selectedUnlockedIndex]?.media_type === "video" || 
                 unlockedContent[selectedUnlockedIndex]?.image_data?.startsWith("data:video/") ? (
                  <video
                    src={unlockedContent[selectedUnlockedIndex]?.image_data}
                    className={`transition-transform duration-200 ease-out rounded-lg shadow-2xl ${lightboxImageLoading ? 'opacity-0' : 'opacity-100'}`}
                    style={{
                      transform: `scale(${zoomLevel})`,
                      maxWidth: zoomLevel === 1 ? '85vw' : 'none',
                      maxHeight: zoomLevel === 1 ? '70vh' : 'none',
                      objectFit: 'contain'
                    }}
                    controls
                    autoPlay
                    onLoadedData={() => setLightboxImageLoading(false)}
                    data-testid="lightbox-video"
                  />
                ) : (
                  <img
                    src={unlockedContent[selectedUnlockedIndex]?.image_data}
                    alt={unlockedContent[selectedUnlockedIndex]?.title || `Image ${selectedUnlockedIndex + 1}`}
                    className={`transition-transform duration-200 ease-out rounded-lg shadow-2xl ${lightboxImageLoading ? 'opacity-0' : 'opacity-100'}`}
                    style={{
                      transform: `scale(${zoomLevel})`,
                      maxWidth: zoomLevel === 1 ? '85vw' : 'none',
                      maxHeight: zoomLevel === 1 ? '70vh' : 'none',
                      objectFit: 'contain'
                    }}
                    draggable={false}
                    onLoad={() => setLightboxImageLoading(false)}
                    data-testid="lightbox-image"
                  />
                )}
                />
              </motion.div>

              {/* Next Button */}
              {totalUnlockedImages > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goToNextUnlocked();
                  }}
                  className="absolute right-2 sm:right-4 p-2 sm:p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
                  title="Next (→)"
                  data-testid="lightbox-next"
                >
                  <ChevronRight size={28} className="text-white" />
                </button>
              )}
            </div>

            {/* Bottom Info Bar */}
            <div className="p-4 text-center">
              <p className="text-white font-medium">
                {unlockedContent[selectedUnlockedIndex]?.title || "Untitled"}
              </p>
              <div className="flex items-center justify-center gap-4 mt-2 text-sm text-gray-400">
                <span>by {unlockedContent[selectedUnlockedIndex]?.creator_name}</span>
                <span className="flex items-center gap-1 text-amber-400">
                  <Coins className="w-4 h-4" />
                  {unlockedContent[selectedUnlockedIndex]?.cost_paid} credits
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Use ← → to navigate • + - to zoom • Esc to close
              </p>
            </div>

            {/* Thumbnail Strip */}
            {totalUnlockedImages > 1 && (
              <div className="p-4 overflow-x-auto border-t border-white/10">
                <div className="flex gap-2 justify-center">
                  {unlockedContent.map((item, index) => {
                    const isVideo = item.media_type === "video" || item.image_data?.startsWith("data:video/");
                    return (
                      <button
                        key={item.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (index !== selectedUnlockedIndex) {
                            setLightboxImageLoading(true);
                            setSelectedUnlockedIndex(index);
                          }
                          setZoomLevel(1);
                        }}
                        className={`relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 transition-all ${
                          index === selectedUnlockedIndex
                            ? 'ring-2 ring-cyan-400 ring-offset-2 ring-offset-black'
                            : 'opacity-50 hover:opacity-100'
                        }`}
                        data-testid={`lightbox-thumbnail-${index}`}
                      >
                        {isVideo ? (
                          <video
                            src={item.image_data}
                            className="w-full h-full object-cover"
                            muted
                          />
                        ) : (
                          <img
                            src={item.image_data}
                            alt={item.title || `Thumbnail ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        )}
                        {/* Video indicator on thumbnail */}
                        {isVideo && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Social (Followers/Following) Modal */}
      <AnimatePresence>
        {showSocialModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowSocialModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 rounded-xl border border-white/10 w-full max-w-md max-h-[80vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div className="flex gap-2">
                  <button
                    onClick={() => setSocialTab("followers")}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      socialTab === "followers"
                        ? "bg-cyan-500 text-white"
                        : "bg-white/10 text-gray-400 hover:text-white"
                    }`}
                    data-testid="social-modal-tab-followers"
                  >
                    <Users className="w-4 h-4 inline-block mr-2" />
                    Followers ({followCounts.followers})
                  </button>
                  <button
                    onClick={() => setSocialTab("following")}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      socialTab === "following"
                        ? "bg-purple-500 text-white"
                        : "bg-white/10 text-gray-400 hover:text-white"
                    }`}
                    data-testid="social-modal-tab-following"
                  >
                    <UserPlus className="w-4 h-4 inline-block mr-2" />
                    Following ({followCounts.following})
                  </button>
                </div>
                <button
                  onClick={() => setShowSocialModal(false)}
                  className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
                  data-testid="social-modal-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {/* Followers List */}
                {socialTab === "followers" && (
                  <div>
                    {loadingFollowers ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
                      </div>
                    ) : followers.length > 0 ? (
                      <div className="space-y-3">
                        {followers.map((follower) => (
                          <div
                            key={follower.id}
                            className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700"
                            data-testid={`follower-${follower.id}`}
                          >
                            <Link
                              to={follower.landing_page_slug ? `/u/${follower.landing_page_slug}` : "#"}
                              onClick={() => setShowSocialModal(false)}
                              className="flex items-center gap-3 flex-1 min-w-0"
                            >
                              {follower.profile_image ? (
                                <img
                                  src={getImageSrc(follower.profile_image)}
                                  alt={follower.name}
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 flex items-center justify-center">
                                  <User className="w-5 h-5 text-white" />
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="text-white font-medium truncate text-sm">{follower.name}</p>
                                <p className="text-gray-400 text-xs truncate">
                                  {follower.role === "creator" && (
                                    <span className="text-cyan-400 mr-1">Creator</span>
                                  )}
                                  @{follower.landing_page_slug || follower.email?.split("@")[0]}
                                </p>
                              </div>
                            </Link>
                            {follower.id !== user?.id && (
                              <button
                                onClick={() => follower.is_following ? handleUnfollow(follower.id) : handleFollowFromList(follower.id)}
                                className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                                  follower.is_following
                                    ? "bg-gray-600 text-white hover:bg-red-500"
                                    : "bg-cyan-500 text-white hover:bg-cyan-600"
                                }`}
                                data-testid={`follow-btn-${follower.id}`}
                              >
                                {follower.is_following ? "Following" : "Follow"}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-14 h-14 bg-gray-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Users className="w-7 h-7 text-gray-500" />
                        </div>
                        <p className="text-gray-400 text-sm">No followers yet</p>
                        <p className="text-gray-500 text-xs mt-1">Share your profile to get followers!</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Following List */}
                {socialTab === "following" && (
                  <div>
                    {loadingFollowing ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
                      </div>
                    ) : following.length > 0 ? (
                      <div className="space-y-3">
                        {following.map((followed) => (
                          <div
                            key={followed.id}
                            className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700"
                            data-testid={`following-${followed.id}`}
                          >
                            <Link
                              to={followed.landing_page_slug ? `/u/${followed.landing_page_slug}` : "#"}
                              onClick={() => setShowSocialModal(false)}
                              className="flex items-center gap-3 flex-1 min-w-0"
                            >
                              {followed.profile_image ? (
                                <img
                                  src={getImageSrc(followed.profile_image)}
                                  alt={followed.name}
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 flex items-center justify-center">
                                  <User className="w-5 h-5 text-white" />
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="text-white font-medium truncate text-sm">{followed.name}</p>
                                <p className="text-gray-400 text-xs truncate">
                                  {followed.role === "creator" && (
                                    <span className="text-cyan-400 mr-1">Creator</span>
                                  )}
                                  @{followed.landing_page_slug || followed.email?.split("@")[0]}
                                </p>
                              </div>
                            </Link>
                            <button
                              onClick={() => handleUnfollow(followed.id)}
                              className="px-2 py-1 rounded-lg text-xs font-medium bg-gray-600 text-white hover:bg-red-500 transition-colors"
                              data-testid={`unfollow-btn-${followed.id}`}
                            >
                              Unfollow
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-14 h-14 bg-gray-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
                          <UserPlus className="w-7 h-7 text-gray-500" />
                        </div>
                        <p className="text-gray-400 text-sm">Not following anyone yet</p>
                        <p className="text-gray-500 text-xs mt-1">Explore creators to follow!</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Buy Credits Modal */}
      <BuyCreditsModal
        isOpen={showBuyCreditsModal}
        onClose={() => setShowBuyCreditsModal(false)}
        currentCredits={user?.credits || 0}
        onSuccess={() => {
          refreshUser();
          setShowBuyCreditsModal(false);
        }}
      />
    </div>
  );
};

export default ProfilePage;
